/**
 * What each phase of an automated session actually does.
 *
 * The flight recorder stores a bare phase slug per row (`part-c`, `phase-3`),
 * which tells a reader nothing about what the session was doing at the time.
 * These descriptions are transcribed from the command definitions that drive
 * the sessions — keep them in sync when those change:
 *
 *   - engine Parts A–G  → `.claude/commands/_partials/build-session.md`
 *   - CDID phases 1–2   → `.claude/commands/a7y-cdid-session.md`
 *   - AHC phases 1–4    → `.claude/commands/a7y-ahc-session.md`
 *   - APD phases 0–5    → `.claude/commands/a7y-apd-session.md`
 *
 * The numbered phases are per-session-kind and mean entirely different things
 * (`phase-3` is "Survey post" in AHC and "Record deploy queue" in APD),
 * so they are looked up by kind. The engine Parts are shared by CDID and AHC.
 */
import type { AutobuildKind } from '@a7y/types';

export interface PhaseInfo {
  title: string;
  description: string;
  /** 1-based position in the session's documented run order; absent for runner rows. */
  step?: number;
  /** How many steps the session has in total, for "4 of 9". */
  totalSteps?: number;
}

/** Rows that belong to whatever phase was running, rather than starting one. */
const CROSS_CUTTING = new Set([
  'ticket',
  'state-change',
  'start',
  'terminal',
  'heartbeat',
  'progress',
  'stalled',
  'agent-contract',
  'skipped',
  'session',
  'quota-usage',
  'lifecycle',
]);

export function isCrossCutting(phase: string): boolean {
  return CROSS_CUTTING.has(normalizePhase(phase));
}

/** `phase1` appears in a handful of older CDID rows alongside `phase-1`. */
export function normalizePhase(phase: string): string {
  return phase
    .trim()
    .toLowerCase()
    .replace(/^phase(\d)/, 'phase-$1');
}

// Engine parts — shared by the CDID and AHC build sessions.
const ENGINE_PARTS: Record<string, PhaseInfo> = {
  'part-a': {
    title: 'Part A — Preflight',
    description:
      'Checks the repo and environment are fit to run: credentials, clean checkout, no conflicting session already in flight.',
  },
  'part-b': {
    title: 'Part B — Integration branch + worktree',
    description:
      "Cuts the session's integration branch off the newest release branch, creates the worktree, installs dependencies, and builds shared packages.",
  },
  'part-c': {
    title: 'Part C — Implement',
    description:
      'Analyses the batch for overlapping file footprints, duplicates, and ordering, then dispatches each ticket to an agent team. Karen reviews every result before it merges to the integration branch.',
  },
  'part-d': {
    title: 'Part D — Verify (and fix)',
    description:
      'Runs the full verification suite — lint, typecheck, test, build — across every affected project, fixing what it breaks. Nothing is released until this is green.',
  },
  'part-e': {
    title: 'Part E — Finish tickets',
    description:
      'Moves each landed ticket to its terminal state in Linear, posts the summary comment, and removes the ticket worktrees and branches.',
  },
  'part-f': {
    title: 'Part F — Release + staging deploy',
    description:
      'Fast-forwards the release branch to the verified integration branch, confirms the staging deploy was dispatched, and moves on while candidate gates report independently.',
  },
  'part-g': {
    title: 'Part G — Report + hygiene',
    description:
      'Housekeeping and the final report: ticket rows for this console, epic closure, integration-branch cleanup, and the Slack summary.',
  },
};

const CDID_PHASES: Record<string, PhaseInfo> = {
  'phase-1': {
    title: 'Phase 1 — Candidate selection',
    description:
      'Queries TPF-assigned open tickets and applies every gate: runnable project and initiative, start date, work containers, blocked-by relations, and trigger prefixes.',
  },
  'phase-2': {
    title: 'Phase 2 — Eval each candidate',
    description:
      'Judges each surviving candidate on its own merits — is it well-specified enough to implement unattended — and routes it READY, to the human queue, or back for clarification.',
  },
  ...ENGINE_PARTS,
};

const AHC_PHASES: Record<string, PhaseInfo> = {
  'phase-1': {
    title: 'Phase 1 — Health check',
    description:
      "Queries BigQuery for the platform's operational health over the last day: errors, warnings, failures, and cost signals.",
  },
  'phase-2': {
    title: 'Phase 2 — Triage',
    description:
      'Classifies each signal by severity and decides what is noise, what is known, and what needs a ticket.',
  },
  'phase-3': {
    title: 'Phase 3 — Survey post',
    description: 'Posts the health survey to #ops-health-check.',
  },
  'phase-4': {
    title: 'Phase 4 — Ticket creation',
    description: 'Files Linear tickets for moderate and critical issues only.',
  },
  ...ENGINE_PARTS,
};

const APD_PHASES: Record<string, PhaseInfo> = {
  'phase-0': {
    title: 'Phase 0 — Preflight',
    description: 'Confirms the environment and credentials before touching production.',
  },
  'phase-1': {
    title: 'Phase 1 — Freeze candidate',
    description:
      'Validates the annotated candidate tag and exact SHA, including staging attestations, ancestry, and undeployed status.',
  },
  'phase-2': {
    title: 'Phase 2 — Verify exact candidate',
    description:
      'Checks out the immutable candidate SHA detached and runs the full verification suite without patching a rejection.',
  },
  'phase-3': {
    title: 'Phase 3 — Record deploy queue',
    description:
      'Records only tickets reachable from the frozen candidate SHA as the production deploy queue.',
  },
  'phase-4': {
    title: 'Phase 4 — Trigger + monitor the production deploy',
    description:
      'Dispatches deploy-production.yml at the candidate tag with the expected SHA and watches it through post-deploy smoke.',
  },
  'phase-5': {
    title: 'Phase 5 — Validate + report',
    description:
      'Confirms immutable tags and untouched release state, advances exact-SHA tickets, and posts the deploy summary.',
  },
};

const BY_KIND: Record<AutobuildKind, Record<string, PhaseInfo>> = {
  cdid: CDID_PHASES,
  ahc: AHC_PHASES,
  apd: APD_PHASES,
};

/**
 * Written by the runner rather than the session itself, for every kind. These
 * are cross-cutting, so they normally fold into the surrounding phase — but a
 * `start` row precedes every phase and has nothing to fold into.
 */
const RUNNER_PHASES: Record<string, PhaseInfo> = {
  start: {
    title: 'Session start',
    description: 'The runner has claimed the job and the session is booting.',
  },
  terminal: {
    title: 'Session end',
    description:
      "The runner's own record of how the job ended, written even if the session crashed.",
  },
  heartbeat: {
    title: 'Process heartbeat',
    description:
      'The runner process still exists. This is transport liveness only and does not claim that the model made progress.',
  },
  progress: {
    title: 'Semantic progress',
    description:
      'The supervisor observed a new assistant, tool, task, or result boundary since the previous progress row.',
  },
  stalled: {
    title: 'Session stalled',
    description:
      'No semantic progress arrived before the bounded deadline, so the supervisor terminated the owned process group.',
  },
  'agent-contract': {
    title: 'Agent contract failure',
    description:
      'A subagent attempted a capability unavailable in its role; the supervisor preserved the tool, role, and task evidence and stopped the run.',
  },
  skipped: {
    title: 'Session skipped',
    description: 'The run was cancelled before any job started — no session executed.',
  },
};

/**
 * Documented run order per session kind — the sequence each command file
 * prescribes, which is what the step numbers count.
 *
 * **Parts and Phases are one flat sequence, not a hierarchy.** Both CDID and AHC
 * run the engine's Part A *first*, then their own numbered Phases, then Parts
 * B–G ("Run the engine's Part A …, then the … phases below to produce `$BATCH`,
 * then the engine's Parts B–G"). So Part A is not "inside" Phase 1 — it precedes
 * it. Rendering the two as nested would invert the actual order. Numbering by
 * position conveys where a section sits without implying containment.
 *
 * APD is not a build session and has no engine Parts at all — just Phases 0–5.
 */
const RUN_ORDER: Record<AutobuildKind, string[]> = {
  cdid: [
    'part-a',
    'phase-1',
    'phase-2',
    'part-b',
    'part-c',
    'part-d',
    'part-e',
    'part-f',
    'part-g',
  ],
  ahc: [
    'part-a',
    'phase-1',
    'phase-2',
    'phase-3',
    'phase-4',
    'part-b',
    'part-c',
    'part-d',
    'part-e',
    'part-f',
    'part-g',
  ],
  apd: ['phase-0', 'phase-1', 'phase-2', 'phase-3', 'phase-4', 'phase-5'],
};

/**
 * Look up a phase, annotated with its position in the kind's run order. Returns
 * null for unknown slugs so the UI can fall back to the raw value rather than
 * inventing a description for something new.
 */
export function phaseInfo(kind: AutobuildKind, phase: string): PhaseInfo | null {
  const key = normalizePhase(phase);
  const base = BY_KIND[kind]?.[key] ?? RUNNER_PHASES[key] ?? null;
  if (!base) return null;
  const order = RUN_ORDER[kind] ?? [];
  const idx = order.indexOf(key);
  // Runner rows (start / terminal / skipped) are not steps in the sequence.
  if (idx === -1) return base;
  return { ...base, step: idx + 1, totalSteps: order.length };
}
